<script setup lang="ts">
import { ref, onBeforeMount, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { AUTHMODELHS } from '@/utils/models'
import { RECAPTCHA_KEYS } from '@/utils/recaptcha'
import recaptchaService from '@/services/recaptcha.service'
import utilModelsService from '@/services/util.models.service'
import otpServices from '@/services/otp.service'
import authService, { type IForgotPasswordRequest } from '@/services/auth.service'
import { useAuthStore } from '@/stores/auth.store'
import closeIcon from '@/assets/images/svg/AuthOnboarding/CloseButton.svg'
import LoginRegisterBanner from '@/components/header/auth/AuthOnboardingAdvBanner/LoginRegisterBanner.vue'
import AuthOnboardingLabel from '@/components/header/auth/AuthOnboardingLabel.vue'
import Button from '@/components/atoms/button/index.vue'
import ModelLayoutAuth from '@/layouts/ModelLayoutAuth.vue'
import { until } from '@vueuse/core'
import { VueRecaptcha } from 'vue-recaptcha'
import { HttpStatusCode } from 'axios'
import { handleError } from '@/services/errorHandler.service'
import { getUserInvitation } from '@/services/teach.service'

const authStore = useAuthStore()
const route = useRoute()

const disableVerifyButton = ref<boolean>(true)
const sitekey = ref<string | null>(RECAPTCHA_KEYS.RECAPTCHALOGINSITEKEY)
const rechaptha = ref<any>(null)
const recapthaToken = ref<string>('')
const recapthaError = ref<string>('')
const showRecaptcha = ref<boolean>(false)
const isLoading = ref<boolean>(false)
const httpErrorTimeOut = ref<NodeJS.Timeout | number | null>(null)
const httpError = ref<string>('')
const httpSuccessTimeOut = ref<NodeJS.Timeout | number | null>(null)
const httpSuccess = ref<string>('')
const disableResendButton = ref<boolean>(true)
const otpInputs: string[] = []
const otpCombined = ref<string>('')
const resendCountdown = ref<number>(0)
const errorMessage = ref<string>('')

/**
 * Handles the response from the reCAPTCHA verification and sets the token.
 * @param response - The reCAPTCHA response token.
 */
const recaptchaVerified = (response: string) => {
  recapthaToken.value = response
  recapthaError.value = ''
}

/**
 * Handles the reCAPTCHA error and sets the error message.
 * @param error - The reCAPTCHA error message.
 */
const recaptchaError = (error: string) => {
  recapthaToken.value = ''
  recapthaError.value = error
  rechaptha.value.reset()
}

/**
 * @description Focus on next input for OTP
 * @param currentIndex - curent input index
 * @param direction - direction
 */
function focusNextInput(currentIndex: number, direction: number) {
  const targetIndex = currentIndex + direction
  const inputElements = document.querySelectorAll('[data-focus-input-init]')

  const targetElement = inputElements[targetIndex] as HTMLElement
  if (targetElement) {
    targetElement.focus()
  }
}

/**
 * @description handles the key up event while entering OTP
 * @param index - index of the input
 * @param event - event from the keyboard
 */
function handleKeyup(index: number, event: any) {
  const target = event.target
  const direction = target.value.length === 0 ? -1 : 1

  otpInputs[index] = target.value

  focusNextInput(index, direction)

  otpCombined.value = otpInputs.join('')

  if (otpCombined.value.length === 6) {
    disableVerifyButton.value = false
  } else {
    disableVerifyButton.value = true
  }
}

/**
 * @description handles the paste event while entering OTP
 * @param index - index of the input
 * @param event - event from the keyboard
 */
function handlePaste(index: number, event: any) {
  const pastedData = event.clipboardData.getData('text')
  const inputElements = document.querySelectorAll('[data-focus-input-init]')
  if (pastedData.length === 6 && /^\d+$/.test(pastedData)) {
    for (let i = 0; i < pastedData.length; i++) {
      otpInputs[i] = pastedData[i]
    }
    otpCombined.value = otpInputs.join('')
    disableVerifyButton.value = false

    const targetElement = inputElements[6 - 1] as HTMLElement
    if (targetElement) {
      targetElement.focus()
    }
  } else {
    for (let i = 0; i < pastedData.length; i++) {
      otpInputs[i] = ''
    }
    otpCombined.value = ''
    disableVerifyButton.value = true

    const targetElement = inputElements[0] as HTMLElement
    if (targetElement) {
      targetElement.focus()
    }
    event.preventDefault()
  }
}

/**
 * @description Verified Login Otp
 */
const verifyOtpLogin = async () => {
  errorMessage.value = ''
  if (otpCombined.value.length === 6) {
    isLoading.value = true
    await otpServices
      .verifyForgotPasswordOtp(authStore.userEmailOtpVerified as string, otpCombined.value)
      .then((res) => {
        if (res?.status === HttpStatusCode.Ok) {
          utilModelsService.closeModal(AUTHMODELHS.OTP)
          utilModelsService.openModal(AUTHMODELHS.RESETPASSWORD)
        }
      })
      .catch((error) => {
        if (error.response.data.errorCode == 'OTP_DOES_NOT_MATCH') {
          errorMessage.value =
            'Sorry, the OTP you entered is incorrect. Please double-check your OTP and try again.'
          otpInputs.fill('')
        } else {
          handleError(error)
        }
        return error.data
      })
      .finally(() => (isLoading.value = false))
  }
  authStore.setUserEnteredOtp(otpCombined.value)
}

/**
 * @description Verify Registered Api
 */
const verifyOtpRegister = async () => {
  errorMessage.value = ''
  if (otpCombined.value.length === 6) {
    isLoading.value = true
    await otpServices
      .verifyRegisteredPasswordOtp(authStore.userEmailOtpVerified as string, otpCombined.value)
      .then(async (res) => {
        if (res?.status === HttpStatusCode.Ok) {
          await getUserInvitation()
          utilModelsService.closeModal(AUTHMODELHS.OTP)
          utilModelsService.openModal(AUTHMODELHS.OTPVERIFIEDSUCCESS)
        }
      })
      .catch((error) => {
        if (error.response.data.errorCode == 'OTP_DOES_NOT_MATCH') {
          errorMessage.value =
            'Sorry, the OTP you entered is incorrect. Please double-check your OTP and try again.'
        } else {
          handleError(error)
        }
      })
      .finally(() => {
        isLoading.value = false
      })
  }
}

/**
 * @description Generate OTP for account registration
 */
const initiateOtp = async () => {
  errorMessage.value = ''
  isLoading.value = true

  await otpServices
    .resendRegisteredOtp(authStore.userEmailOtpVerified as string)
    .then((res) => {
      if (res?.status === HttpStatusCode.Ok) {
        isLoading.value = false
      }
    })
    .catch((error) => {
      if (error.response.data.errorCode == 'OTP_DOES_NOT_MATCH') {
        errorMessage.value =
          'Sorry, the OTP you entered is incorrect. Please double-check your OTP and try again.'
      } else if (error.response.data.errorCode == 'OTP_ALREADY_GENERATED') {
        errorMessage.value = 'Sorry, the OTP is already generated. Please try again after some time'
      }
      isLoading.value = false
      return error.data
    })
}
/**
 * to resent otp when user forgot password
 */
const initiateOtpForgotPassword = async () => {
  errorMessage.value = ''
  isLoading.value = true
  rechaptha.value.execute()
  await until(recapthaToken).changed()

  if (!recapthaToken.value || recapthaError.value) {
    isLoading.value = false
    return
  }

  const requestPayload: IForgotPasswordRequest = {
    email: authStore.userEmailOtpVerified as string,
    gc: recapthaToken.value
  }

  await authService
    .forgotPassword(requestPayload)
    .then((response: any) => {
      if (response.status === HttpStatusCode.Ok) {
        errorMessage.value = ''
      }
    })
    .catch((error: any) => {
      if (error?.response?.data.errorCode == 'OTP_ALREADY_GENERATED') {
        errorMessage.value =
          'OTP has already been generated. Please wait sometime before generating again'
      }
    })
    .finally(() => {
      recapthaToken.value = ''
      rechaptha.value.reset()
      isLoading.value = false
    })
}

/**
 * @description Resend the OTP
 * @param hasInitiated - has initiated the delete
 */
const handleResend = (hasInitiated: boolean = true) => {
  disableVerifyButton.value = true
  if (hasInitiated === true && authStore.otpRedirectFrom != 'Login') {
    initiateOtp()
  } else {
    initiateOtpForgotPassword()
  }
  disableResendButton.value = false
  resendCountdown.value = 120
  const intervalId = setInterval(() => {
    resendCountdown.value--
    if (resendCountdown.value === 0) {
      clearInterval(intervalId)
      disableResendButton.value = true
      disableVerifyButton.value = false
    }
  }, 1000)
}

/**
 * If prop is true, page retrieves data again from API after changes from modal
 */
const handleLoadDataWhenClose = () => {
  authStore.setRedirectTo(null)
}

onBeforeMount(async () => {
  sitekey.value = await recaptchaService.getRechapthaKey(RECAPTCHA_KEYS.RECAPTCHALOGINSITEKEY)
})
onMounted(async () => {
  await utilModelsService.onOpenModel((hs: string | null) => {
    if ((hs === AUTHMODELHS.FORGOTPASSWORD || hs === AUTHMODELHS.OTP) && !showRecaptcha.value) {
      showRecaptcha.value = true
    }
  })
  watch(httpError, () => {
    if (httpError.value) {
      if (httpErrorTimeOut.value) clearTimeout(httpErrorTimeOut.value)
      httpErrorTimeOut.value = setTimeout(() => {
        httpError.value = ''
      }, 8000)
    }
  })
  watch(httpSuccess, () => {
    if (httpSuccess.value) {
      if (httpSuccessTimeOut.value) clearTimeout(httpSuccessTimeOut.value)
      httpSuccessTimeOut.value = setTimeout(() => {
        httpSuccess.value = ''
      }, 8000)
    }
  })
})
</script>

<template>
  <ModelLayoutAuth :hs="AUTHMODELHS.OTP">
    <div class="flex h-screen w-full flex-col">
      <div class="absolute right-0 flex justify-end">
        <img
          :src="closeIcon"
          alt="Close"
          class="link-primary hs-collapse-toggle mr-5 mt-2 h-[20px] w-[20px] cursor-pointer transition-all"
          :data-hs-overlay="`#${AUTHMODELHS.OTP}`"
          @click="handleLoadDataWhenClose"
          v-show="!(route.name === 'extensionLogin' && !route.query.register)"
        />
      </div>
      <div class="flex w-full flex-1 p-1 sm:p-5">
        <div class="hidden w-full rounded-3xl bg-[#EBEFF6] md:hidden lg:block lg:min-w-[45%]">
          <LoginRegisterBanner
            text="Share your best code in a few seconds!"
            highlighertext="few seconds!"
          />
        </div>

        <div class="mt-5 h-full w-full rounded-md p-1 sm:p-5 lg:min-w-[50%]">
          <div class="mt-10 flex items-center justify-center sm:mt-0">
            <AuthOnboardingLabel
              title="Verify your email"
              description="Hear that? Your email is buzzing with an OTP. This code is valid for the next"
              customstyle="ml-5 mt-5 text-lg font-medium leading-7"
              additionaltext="30 minutes."
              customstyleTitle="text-center text-3xl font-medium leading-10"
            />
          </div>
          <div class="mt-5 flex flex-col sm:mt-0 sm:p-5">
            <div class="flex w-full items-center justify-center">
              <div class="flex max-w-[389px] flex-col items-start justify-center">
                <div class="text-start text-lg font-semibold leading-normal">OTP Verification</div>
                <div class="mt-4 text-sm font-normal leading-tight">
                  Please enter the 6-digit verification sent to
                </div>
                <!-- Please help with improvement for below div OTP input -->
                <div>
                  <div class="mt-3 flex space-x-2.5">
                    <!-- PIN Input fields -->
                    <input
                      class="h-14 w-1/6 rounded-xl border border-[#DDD] bg-[#FCFCFC] text-center font-medium text-text-primary outline-none focus:border-blue-500 focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50"
                      v-for="(input, index) in 6"
                      :key="index"
                      :id="`input-${index}`"
                      :value="otpInputs[index]"
                      @keyup="handleKeyup(index, $event)"
                      @paste="handlePaste(index, $event)"
                      data-focus-input-init
                      type="text"
                      maxlength="1"
                    />
                  </div>
                  <div class="flex justify-end">
                    <p v-if="!disableResendButton" class="p-xsmall mt-2 text-gray-400">
                      Resend OTP in {{ resendCountdown }} seconds
                    </p>
                    <Button
                      v-else
                      type="button"
                      color="secondary"
                      variant="link"
                      class="mt-5 p-0 font-normal"
                      :disabled="!disableResendButton"
                      @click="handleResend(true)"
                    >
                      Resend Code ?
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <div class="mt-6 text-center" v-show="errorMessage">
              <FontAwesomeIcon icon="fa-circle-exclamation" class="text-md mr-2 text-red-500" />
              <span class="whitespace-break-spaces text-sm font-medium">
                {{ errorMessage }}
              </span>
            </div>
            <div class="mt-6 text-center" v-show="isLoading">
              <span class="text-md whitespace-break-spaces font-medium text-sky-950">
                Checking your OTP. Please wait...
              </span>
            </div>
            <div class="mt-10 flex justify-center lg:mx-32">
              <Button
                @click="
                  authStore.otpRedirectFrom == 'Login' ? verifyOtpLogin() : verifyOtpRegister()
                "
                :disabled="disableVerifyButton"
                color="secondary"
                variant="filled"
                size="medium"
                class="mt-6 h-[48px] sm:w-[171px]"
              >
                Verify OTP
              </Button>
            </div>

            <vue-recaptcha
              v-if="showRecaptcha"
              size="invisible"
              ref="rechaptha"
              :sitekey="sitekey || ''"
              @verify="recaptchaVerified"
              @fail="recaptchaError('Recaptcha failed. Please verify again.')"
              @error="recaptchaError"
            />
            <p :class="['p-xsmall ', 'error', { hidden: !recapthaError }]">
              {{ recapthaError }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <!-- form -->
  </ModelLayoutAuth>
</template>
